var __jsx = React.createElement;
import { CloseOutlined, DownOutlined, FacebookOutlined, InstagramOutlined, LinkedinOutlined, MenuOutlined, TwitterOutlined, WhatsAppOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { AppContext } from "../DixuApp/DixuApp";
import DixuButton from "../DixuButton/DixuButton";
import DixuContainer from "../DixuContainer/DixuContainer";
import DixuDropdown from "../DixuDropdown/DixuDropdown";
import { DixuCol, DixuRow } from "../DixuGrid/DixuGrid";
import DixuLink from "../DixuLink/DixuLink";
import DixuMenu from "../DixuMenu/DixuMenu";
import DixuProfileInfo from "../DixuProfileInfo/DixuProfileInfo";
import DixuText from "../DixuText/DixuText";
import usePreventBodyScroll from "../../hooks/usePreventBodyScroll";
import useRestrictFocus from "../../hooks/useRestrictFocus";
import useViewportHeight from "../../hooks/useViewportHeight";
import Router, { useRouter } from 'next/router';
import DixuLogo from "../../public/icons/dixu.svg";
import React, { useContext, useEffect, useRef, useState } from 'react';
import { DIXU_CONTACT_INFO } from "../../utils/DixuConstants";
import { toRem } from "../../utils/Utils";
const DixuMainNav = _ref => {
  let {
    scrollUpThreshold,
    scrollDownThreshold,
    colorScheme,
    solid,
    translucent,
    autoHide,
    position,
    className
  } = _ref;
  const navRef = useRef();
  const navBarRef = useRef();
  const {
    0: navHeight,
    1: setNavHeight
  } = useState(0);
  const {
    0: scrolled,
    1: setScrolled
  } = useState(false);
  const {
    0: hidden,
    1: setHidden
  } = useState(false);
  const {
    0: open,
    1: setOpen
  } = useState(false);
  const {
    user
  } = useContext(AppContext);
  const router = useRouter();
  const previousPathnameRef = useRef(router?.pathname);
  usePreventBodyScroll(open);
  useRestrictFocus({
    condition: open,
    getHostElement: () => navRef.current
  });
  useViewportHeight({
    condition: open,
    cssVariableName: 'dixu-main-nav-open-height',
    transform: height => height - navHeight
  });
  const classes = classNames('dixu-main-nav', className, {
    'dixu-main-nav--scrolled': scrolled,
    'dixu-main-nav--solid': open || solid,
    'dixu-main-nav--translucent': translucent,
    'dixu-main-nav--hidden': !open && hidden,
    'dixu-main-nav--open': open,
    [`dixu-main-nav--position-${position}`]: position,
    [`dixu-main-nav--color-scheme-${colorScheme}`]: colorScheme
  });
  const color = colorScheme === 'light' ? '2-8' : '2-1';
  const items = [[{
    condition: !user,
    href: '/login',
    label: 'Kirjaudu',
    desktop: false,
    noRouterLink: true
  }, {
    condition: !!user,
    href: '/oma-dixu',
    label: 'OmaDixu',
    desktop: false
  }, {
    href: '/myytavat-asunnot',
    label: 'Asunnot'
  }, {
    href: '/myy-asuntosi',
    label: 'Myy asuntosi',
    subitems: [{
      href: '/myy-asuntosi',
      label: 'Näin myyt asuntosi'
    }, {
      href: '/hinnasto',
      label: 'Hinnasto'
    }, {
      href: '/asiakaskokemukset',
      label: 'Asiakaskokemuksia'
    }, {
      href: '/yhteistyokumppanit',
      label: 'Yhteistyökumppanit'
    }]
  }, {
    href: '/valittajalle',
    label: 'Välittäjälle'
  }], [{
    href: '/meista',
    label: 'Yritys',
    subitems: [{
      href: '/meista',
      label: 'Yritys'
    }, {
      href: '/ota-yhteytta',
      label: 'Ota yhteyttä'
    }, {
      href: '/faq',
      label: 'UKK'
    }, {
      href: '/yhteistyokumppanit',
      label: 'Yhteistyökumppanit'
    }]
  }], [{
    condition: !!user,
    href: '/logout',
    label: 'Kirjaudu ulos',
    desktop: false,
    noRouterLink: true
  }]];
  const socialItems = [{
    icon: /*#__PURE__*/React.createElement(WhatsAppOutlined, null),
    href: DIXU_CONTACT_INFO.whatsapp,
    label: 'WhatsApp'
  }, {
    icon: /*#__PURE__*/React.createElement(TwitterOutlined, null),
    href: DIXU_CONTACT_INFO.twitter,
    label: 'Twitter'
  }, {
    icon: /*#__PURE__*/React.createElement(FacebookOutlined, null),
    href: DIXU_CONTACT_INFO.facebook,
    label: 'Facebook'
  }, {
    icon: /*#__PURE__*/React.createElement(InstagramOutlined, null),
    href: DIXU_CONTACT_INFO.instagram,
    label: 'Instagram'
  }, {
    icon: /*#__PURE__*/React.createElement(LinkedinOutlined, null),
    href: DIXU_CONTACT_INFO.linkedin,
    label: 'LinkedIn'
  }].filter(item => {
    const {
      href
    } = item;
    return !!href;
  }).map(link => {
    const {
      icon,
      href,
      label
    } = link;
    return /*#__PURE__*/React.createElement(DixuCol, {
      key: label
    }, /*#__PURE__*/React.createElement(DixuButton, {
      icon: icon,
      href: href,
      "aria-label": label,
      block: false,
      type: "outline",
      modifierType: "circle",
      font: "3",
      shadow: "1",
      noBorder: true,
      external: true
    }));
  });
  const includedItems = items.map(itemGroup => {
    return itemGroup.filter(item => {
      const {
        condition
      } = item;
      if (typeof condition === 'undefined') {
        return true;
      }
      return condition;
    });
  });
  const mobileItems = includedItems.map(itemGroup => {
    return itemGroup.filter(item => {
      const {
        mobile
      } = item;
      if (typeof mobile === 'undefined') {
        return true;
      }
      return mobile;
    });
  }).filter(e => e.length > 0).map((itemGroup, index) => {
    const mappedItems = itemGroup.map(item => {
      const {
        href,
        label,
        external,
        subitems,
        noRouterLink
      } = item;
      const hasSubitems = !!subitems?.length;
      const link = /*#__PURE__*/React.createElement(DixuLink, {
        color: color,
        font: "3",
        weight: "bold",
        align: "center",
        href: hasSubitems ? '' : href,
        external: external,
        noRouterLink: noRouterLink,
        onClick: event => {
          if (hasSubitems) {
            event.preventDefault();
          }
        }
      }, label, hasSubitems && /*#__PURE__*/React.createElement(DixuText, {
        font: "1",
        style: {
          verticalAlign: 'middle'
        }
      }, /*#__PURE__*/React.createElement(DownOutlined, {
        style: {
          position: 'relative',
          left: toRem(4)
        }
      })));
      const navItem = (() => {
        if (hasSubitems) {
          const menuItems = subitems.map(subitem => {
            const {
              href: subitemHref,
              label: subitemLabel,
              external: subitemExternal,
              noRouterLink: subitemNoRouterLink
            } = subitem;
            return /*#__PURE__*/React.createElement(DixuMenu.Item, {
              key: subitemLabel
            }, /*#__PURE__*/React.createElement(DixuLink, {
              href: subitemHref,
              font: "3",
              color: "2-8",
              align: "center",
              external: subitemExternal,
              noRouterLink: subitemNoRouterLink
            }, subitemLabel));
          });
          const menu = /*#__PURE__*/React.createElement(DixuMenu, {
            forceSubMenuRender: true
          }, menuItems);
          return /*#__PURE__*/React.createElement(DixuDropdown, {
            overlay: menu
          }, /*#__PURE__*/React.createElement("div", null, link));
        }
        return link;
      })();
      return /*#__PURE__*/React.createElement(DixuCol, {
        key: label,
        span: 24
      }, navItem);
    });
    return /*#__PURE__*/React.createElement(DixuCol, {
      key: index.toString(36),
      span: 24
    }, /*#__PURE__*/React.createElement(DixuRow, {
      gutter: [0, 36]
    }, mappedItems));
  });
  const desktopItems = includedItems.map(itemGroup => {
    return itemGroup.filter(item => {
      const {
        desktop
      } = item;
      if (typeof desktop === 'undefined') {
        return true;
      }
      return desktop;
    });
  }).filter(e => e.length > 0).map((itemGroup, index) => {
    const mappedItems = itemGroup.map(item => {
      const {
        href,
        label,
        external,
        subitems,
        noRouterLink
      } = item;
      const hasSubitems = !!subitems?.length;
      const link = /*#__PURE__*/React.createElement(DixuLink, {
        color: color,
        font: "3",
        weight: "bold",
        href: hasSubitems ? '' : href,
        external: external,
        noRouterLink: noRouterLink,
        onClick: event => {
          if (hasSubitems) {
            event.preventDefault();
          }
        }
      }, /*#__PURE__*/React.createElement(DixuRow, {
        align: "middle",
        gutter: 6,
        wrap: false
      }, /*#__PURE__*/React.createElement(DixuCol, null, label), hasSubitems && /*#__PURE__*/React.createElement(DixuCol, null, /*#__PURE__*/React.createElement(DixuText, {
        font: "1"
      }, /*#__PURE__*/React.createElement(DownOutlined, null)))));
      const navItem = (() => {
        if (hasSubitems) {
          const menuItems = subitems.map(subitem => {
            const {
              href: subitemHref,
              label: subitemLabel,
              external: subitemExternal,
              noRouterLink: subitemNoRouterLink
            } = subitem;
            return /*#__PURE__*/React.createElement(DixuMenu.Item, {
              key: subitemLabel
            }, /*#__PURE__*/React.createElement(DixuLink, {
              href: subitemHref,
              font: "3",
              color: "2-8",
              external: subitemExternal,
              noRouterLink: subitemNoRouterLink
            }, subitemLabel));
          });
          const menu = /*#__PURE__*/React.createElement(DixuMenu, {
            forceSubMenuRender: true
          }, menuItems);
          return /*#__PURE__*/React.createElement(DixuDropdown, {
            overlay: menu
          }, /*#__PURE__*/React.createElement("div", null, link));
        }
        return link;
      })();
      return /*#__PURE__*/React.createElement("li", {
        key: label,
        className: "dixu-main-nav__item"
      }, navItem);
    });
    return /*#__PURE__*/React.createElement("ul", {
      key: index.toString(36),
      className: "dixu-main-nav__item-group"
    }, mappedItems);
  });
  useEffect(() => {
    const updateNavHeight = () => {
      const element = navBarRef.current;
      if (!element) {
        return undefined;
      }
      const {
        height
      } = element.getBoundingClientRect();
      setNavHeight(height);
      return undefined;
    };
    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);
    return () => {
      window.removeEventListener('resize', updateNavHeight);
    };
  }, []);
  useEffect(() => {
    const updateScrolled = () => {
      const currentScrollPos = window.pageYOffset;
      if (currentScrollPos > 0) {
        if (!scrolled) {
          setScrolled(true);
        }
      } else if (scrolled) {
        setScrolled(false);
      }
    };
    updateScrolled();
    document.addEventListener('scroll', updateScrolled);
    return () => {
      document.removeEventListener('scroll', updateScrolled);
    };
  }, [scrolled]);
  useEffect(() => {
    const element = navRef.current;
    if (!element || !autoHide) {
      return undefined;
    }
    let prevScrollPos = window.pageYOffset;
    let scrollUpStartPos;
    let scrollDownStartPos;
    const handleHideNav = () => {
      const currentScrollPos = window.pageYOffset;
      const elHeight = element.getBoundingClientRect().height;
      if (currentScrollPos <= prevScrollPos) {
        if (typeof scrollUpStartPos === 'undefined') {
          scrollUpStartPos = currentScrollPos;
        }
        scrollDownStartPos = undefined;
        if (hidden && currentScrollPos < scrollUpStartPos - scrollUpThreshold || currentScrollPos < elHeight) {
          setHidden(false);
        }
      } else {
        if (typeof scrollDownStartPos === 'undefined') {
          scrollDownStartPos = currentScrollPos;
        }
        scrollUpStartPos = undefined;
        if (!hidden && currentScrollPos > elHeight && currentScrollPos > scrollDownStartPos + scrollDownThreshold) {
          setHidden(true);
        }
      }
      prevScrollPos = currentScrollPos;
    };
    window.addEventListener('scroll', handleHideNav);
    return () => {
      window.removeEventListener('scroll', handleHideNav);
    };
  }, [scrollUpThreshold, scrollDownThreshold, scrolled, hidden, autoHide]);
  useEffect(() => {
    const closeNav = () => {
      setOpen(false);
    };
    const resetNav = () => {
      const {
        pathname
      } = Router;
      if (pathname !== previousPathnameRef.current) {
        setHidden(false);
        setOpen(false);
      }
      previousPathnameRef.current = pathname;
    };
    Router?.events?.on('routeChangeStart', closeNav);
    Router?.events?.on('hashChangeStart', closeNav);
    Router?.events?.on('routeChangeComplete', resetNav);
    Router?.events?.on('hashChangeComplete', resetNav);
    return () => {
      Router?.events?.off('routeChangeStart', closeNav);
      Router?.events?.off('hashChangeStart', closeNav);
      Router?.events?.off('routeChangeComplete', resetNav);
      Router?.events?.off('hashChangeComplete', resetNav);
    };
  }, [Router]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("nav", {
    ref: navRef,
    className: classes,
    "aria-label": "Main"
  }, /*#__PURE__*/React.createElement("div", {
    ref: navBarRef
  }, /*#__PURE__*/React.createElement(DixuContainer, {
    horizontalPadding: "xxl",
    verticalPadding: "lg",
    maxWidth: false
  }, /*#__PURE__*/React.createElement("div", {
    className: "dixu-main-nav__content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "dixu-main-nav__logo-wrapper"
  }, /*#__PURE__*/React.createElement(DixuLink, {
    href: "/",
    "aria-label": "Dixu"
  }, /*#__PURE__*/React.createElement(DixuLogo, null))), /*#__PURE__*/React.createElement("div", {
    className: "dixu-main-nav__items"
  }, desktopItems), /*#__PURE__*/React.createElement("div", {
    className: "dixu-main-nav__actions"
  }, user && /*#__PURE__*/React.createElement(DixuDropdown, {
    overlay: /*#__PURE__*/React.createElement(DixuMenu, {
      forceSubMenuRender: true
    }, /*#__PURE__*/React.createElement(DixuMenu.Item, null, /*#__PURE__*/React.createElement(DixuLink, {
      href: "/oma-dixu",
      font: "3",
      color: "2-8"
    }, "OmaDixu")), /*#__PURE__*/React.createElement(DixuMenu.Item, null, /*#__PURE__*/React.createElement(DixuLink, {
      href: "/logout",
      font: "3",
      color: "2-8",
      noRouterLink: true
    }, "Kirjaudu ulos")))
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(DixuLink, {
    color: color,
    font: "1",
    weight: "bold",
    href: "",
    block: true
  }, /*#__PURE__*/React.createElement(DixuRow, {
    align: "middle",
    gutter: 6,
    wrap: false
  }, /*#__PURE__*/React.createElement(DixuCol, null, /*#__PURE__*/React.createElement(DixuProfileInfo, {
    type: "compact",
    href: ""
  })), /*#__PURE__*/React.createElement(DixuCol, null, /*#__PURE__*/React.createElement(DownOutlined, null)))))), !user && /*#__PURE__*/React.createElement(DixuButton, {
    href: "/login",
    target: "_self",
    type: "primary",
    size: "lg",
    noRouterLink: true
  }, "Kirjaudu")), /*#__PURE__*/React.createElement(DixuButton, {
    type: "link",
    block: false,
    noPadding: true,
    className: "dixu-main-nav__menu-button",
    onClick: () => setOpen(!open)
  }, /*#__PURE__*/React.createElement(DixuText, {
    font: "5",
    color: color
  }, open ? /*#__PURE__*/React.createElement(CloseOutlined, null) : /*#__PURE__*/React.createElement(MenuOutlined, null)))))), open && /*#__PURE__*/React.createElement("div", {
    className: "dixu-main-nav__menu-container"
  }, /*#__PURE__*/React.createElement(DixuContainer, {
    horizontalPadding: "xxl",
    verticalPadding: "xxxl",
    maxWidth: "xxl"
  }, /*#__PURE__*/React.createElement(DixuRow, {
    gutter: [0, 50]
  }, /*#__PURE__*/React.createElement(DixuCol, {
    span: 24
  }, /*#__PURE__*/React.createElement(DixuRow, {
    gutter: [0, 46]
  }, mobileItems)), /*#__PURE__*/React.createElement(DixuCol, {
    span: 24
  }, /*#__PURE__*/React.createElement(DixuRow, {
    align: "center",
    gutter: [24, 16]
  }, socialItems)))))));
};
DixuMainNav.defaultProps = {
  scrollUpThreshold: 256,
  scrollDownThreshold: 0,
  colorScheme: 'light',
  solid: true,
  translucent: true,
  autoHide: true,
  position: 'sticky'
};
export default DixuMainNav;